import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Form } from 'react-bootstrap';
import Container from 'react-bootstrap/Container';
import { PARTICIPATION_PATHS } from '../../../config/constants';

export const PARTICIPATION_COMMON_FIELDS = {
  TITLE: 'title',
  MANAGER_NAME: 'managerName',
  REFERENCE: 'reference',
  COMPLETION_STATUS: 'completion-status',
};
export const PARTICIPATION_COMPLETION_STATUS = {
  COMPLETE: 'Complete',
  NOT_STARTED: 'Not Started',
  IN_PROGRESS: 'In Progress',
};

export const getParticipationCompletionStatus = (participant) => {
  if (participant.assessmentsSubmitted >= participant.assessmentsAssigned) {
    return PARTICIPATION_COMPLETION_STATUS.COMPLETE;
  } else if (participant.assessmentsStarted) {
    return PARTICIPATION_COMPLETION_STATUS.IN_PROGRESS;
  }
  return PARTICIPATION_COMPLETION_STATUS.NOT_STARTED;
};

export const getInitialParticipationCompletionStatusModel = (filterReference) => {
  return [
    {
      value: PARTICIPATION_COMPLETION_STATUS.COMPLETE,
      lcValue: PARTICIPATION_COMPLETION_STATUS.COMPLETE.toLowerCase(),
      checked: filterReference === PARTICIPATION_PATHS.COMPLETE,
    },
    {
      value: PARTICIPATION_COMPLETION_STATUS.IN_PROGRESS,
      lcValue: PARTICIPATION_COMPLETION_STATUS.IN_PROGRESS.toLowerCase(),
      checked: filterReference === PARTICIPATION_PATHS.IN_PROGRESS,
    },
    {
      value: PARTICIPATION_COMPLETION_STATUS.NOT_STARTED,
      lcValue: PARTICIPATION_COMPLETION_STATUS.NOT_STARTED.toLowerCase(),
      checked: filterReference === PARTICIPATION_PATHS.NOT_STARTED,
    },
  ];
};

const getValue = (data, field, filterParams) => {
  if (!filterParams?.isMetadata) {
    switch (field) {
      case PARTICIPATION_COMMON_FIELDS.TITLE:
        return data.position.title;
      case PARTICIPATION_COMMON_FIELDS.MANAGER_NAME:
        return data.position.managerName;
      case PARTICIPATION_COMMON_FIELDS.REFERENCE:
        return data.position.reference;
      case PARTICIPATION_COMMON_FIELDS.COMPLETION_STATUS:
        return getParticipationCompletionStatus(data);
    }
  } else {
    const metadata = data.metadata;
    if (metadata) {
      return metadata[filterParams.attribute.name]?.value;
    }
  }
};

// eslint-disable-next-line react/display-name
export const gridFilter = forwardRef((props, ref) => {
  const [allChecked, setAllChecked] = useState(true);
  const [selections, setSelections] = useState([]);
  const [columnField, setColumnField] = useState();

  useImperativeHandle(ref, () => {
    return {
      doesFilterPass(params) {
        if (allChecked) {
          return true;
        }
        const rowValue = getValue(params.data, columnField, props.colDef.filterParams);
        if (rowValue) {
          for (const selection of selections) {
            if (selection.checked && selection.lcValue === rowValue.toLowerCase()) {
              return true;
            }
          }
        }
        return false;
      },

      isFilterActive() {
        return !allChecked;
      },

      getModel() {
        if (!allChecked) {
          return selections;
        }
      },

      setModel(model) {
        setSelections(model);
        if (model?.length) {
          for (const item of model) {
            if (item.checked) {
              setAllChecked(false);
              break;
            }
          }
        }
      },
    };
  });

  const allChanged = (event) => {
    const checked = event.target.checked;
    setAllChecked(checked);
    const updatedSelections = [...selections];
    updatedSelections.forEach((selection) => {
      selection.checked = checked;
    });
    setSelections(updatedSelections);
  };

  const selectionChanged = (event, item) => {
    item.checked = event.target.checked;
    let allChecked = true;
    const updatedSelections = [...selections];
    updatedSelections.forEach((selection) => {
      if (!selection.checked) {
        allChecked = false;
      }
    });
    setSelections(updatedSelections);
    setAllChecked(allChecked);
  };

  useEffect(() => {
    const distinctData = [];
    const items = [];
    const fieldName = props.colDef.field;
    setColumnField(fieldName);
    props.api.forEachNode((node) => {
      const value = getValue(node.data, fieldName, props.colDef.filterParams);
      if (value && !distinctData.includes(value)) {
        distinctData.push(value);
        items.push({
          value,
          lcValue: value.toLowerCase(),
          checked: true,
        });
      }
    });
    items.sort((a, b) => {
      const lcA = a.value.toLowerCase();
      const lcB = b.value.toLowerCase();
      if (lcA > lcB) {
        return 1;
      } else if (lcA < lcB) {
        return -1;
      }
      return 0;
    });
    setSelections(items);
  }, []);

  useEffect(() => {
    props.filterChangedCallback();
  }, [allChecked, selections]);

  return (
    <Container className="pt-1 pb-1">
      <Form className="pt-2">
        <Form.Check
          style={{ minWidth: '15ch' }}
          type="checkbox"
          id="all"
          label="Select All"
          checked={allChecked}
          onChange={allChanged}
        />
      </Form>
      <hr className="mt-2" />
      {selections.map((item, index) => (
        <Form.Check
          className="filterSelection"
          type="checkbox"
          key={index}
          id={index}
          label={item.value}
          checked={selections[index].checked}
          onChange={(event) => {
            selectionChanged(event, item);
          }}
        />
      ))}
    </Container>
  );
});
